import { AlertTriangle, X } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { EVENTS } from '@apps/web/lib/events';
import { cn } from '@apps/web/lib/utils';
import { type Shift } from '@libraries/typescript/api_client';

type MissingShiftWarningBannerProps = {
    readonly missingShifts: ReadonlyArray<
        Pick<Shift, 'id' | 'patient_name' | 'shift_start_time'>
    >;
    readonly organizationId: string | null;
    readonly surface: 'create_outreach_modal';
    readonly ehrDisplayName?: string | null;
    readonly className?: string;
};

/**
 * Warning shown in the create-outreach modal when selected shifts are no
 * longer available in the source EHR.
 */
export function MissingShiftWarningBanner({
    missingShifts,
    organizationId,
    surface,
    ehrDisplayName,
    className,
}: MissingShiftWarningBannerProps) {
    const posthog = usePostHog();
    const [dismissed, setDismissed] = useState(false);

    // Fire a single "shown" event per distinct set of missing shift
    // ids. Re-fire when the set changes; no-op on re-renders that
    // don't change the set. Depend only on the stable key string so
    // parent array-reference churn doesn't re-run the effect.
    const lastLoggedKeyRef = useRef<string | null>(null);
    const sortedMissingShiftIds = useMemo(
        () =>
            missingShifts
                .map((shift) => shift.id)
                .slice()
                .sort(),
        [missingShifts],
    );
    const missingShiftIdsKey = sortedMissingShiftIds.join(',');

    useEffect(() => {
        if (missingShiftIdsKey === '') {
            lastLoggedKeyRef.current = null;
            return;
        }
        if (lastLoggedKeyRef.current === missingShiftIdsKey) {
            return;
        }
        lastLoggedKeyRef.current = missingShiftIdsKey;
        setDismissed(false);
        posthog?.capture(EVENTS.MISSING_SHIFT_OUTREACH_BANNER_SHOWN, {
            organization_id: organizationId,
            surface,
            shift_ids: sortedMissingShiftIds,
            shift_count: sortedMissingShiftIds.length,
        });
    }, [
        missingShiftIdsKey,
        sortedMissingShiftIds,
        organizationId,
        posthog,
        surface,
    ]);

    if (missingShifts.length === 0 || dismissed) {
        return null;
    }

    const handleDismiss = () => {
        setDismissed(true);
        posthog?.capture(EVENTS.MISSING_SHIFT_OUTREACH_BANNER_DISMISSED, {
            organization_id: organizationId,
            surface,
            shift_ids: missingShifts.map((shift) => shift.id),
            shift_count: missingShifts.length,
        });
    };

    return (
        <div
            className={cn(
                'rounded-lg border border-amber-200 bg-amber-50 p-3',
                className,
            )}
            role="alert"
            data-testid="missing-shift-warning-banner"
        >
            <div className="flex items-start gap-2">
                <AlertTriangle
                    className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600"
                    aria-hidden={true}
                />
                <div className="flex-1 space-y-1">
                    <p className="text-sm font-medium text-amber-800">
                        {`${missingShifts.length === 1 ? '1 shift is' : `${missingShifts.length} shifts are`} not included in this outreach as ${missingShifts.length === 1 ? 'it no longer appears' : 'they no longer appear'} in `}
                        {ehrDisplayName ?? 'the source EHR'}
                    </p>
                </div>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleDismiss}
                    className="h-6 w-6 flex-shrink-0 p-0 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                    aria-label="Dismiss missing-shift warning"
                >
                    <X className="h-4 w-4" aria-hidden={true} />
                </Button>
            </div>
        </div>
    );
}
